import { cloneDeep as _cloneDeep, map, pick, values } from 'lodash-es';

import {
  NameQueryFieldValue
} from 'app/query-common/components/name-query-field-input/name-query-field-input.component';
import {
  ICalendarEventResponseSubqueryQueryField
} from 'app/query-common/components/calendar-event-response-subquery-input/CalendarEventResponseFieldViewModel';
import {
  IEntityConnectionSubqueryQueryField
} from 'app/query-common/components/entity-connection-subquery-input/EntityConnectionFieldViewModel';
import { isNilOrEmpty } from 'helpers';
import { Rule, RuleSet } from 'query-builder/query.interfaces';

/**
 * Used to remove superfluous rules before sending the query
 * to the server.
 */
export class SimpleQueryReducer {

  field_reducers = {
    assessment_with_date: (rule: Rule) => this.shouldRemoveAssessmentAdvancedRule(rule),
    assessment: (rule: Rule) => this.isEmptyRule(rule),
    entity_type: (rule: Rule) => this.isEmptyRule(rule),
    follow_up: (rule: Rule) => this.shouldRemoveFollowUpRule(rule),
    follow_up_by: (rule: Rule) => this.shouldRemoveFollowUpRule(rule),
    preferred_language: (rule: Rule) => this.isEmptyRule(rule),
    is_user: (rule: Rule) => this.isEmptyRule(rule),
    last_updated_offset: (rule: Rule) => this.shouldRemoveLastUpdatedOffset(rule),
    relationship_with: (rule: Rule) => this.isEmptyRule(rule),
    connection_with: (rule: Rule) => this.isEmptyRule(rule),
    in_turf: (rule: Rule) => this.isEmptyRule(rule),
    created_at: (rule: Rule) => this.isEmptyRule(rule),
    update_at: (rule: Rule) => this.isEmptyRule(rule),
    email_simple: (rule: Rule) => this.shouldRemoveSimpleContactInfoRule(rule),
    phone_simple: (rule: Rule) => this.shouldRemoveSimpleContactInfoRule(rule),
    social_simple: (rule: Rule) => this.shouldRemoveSimpleSocialRule(rule),
    address_simple: (rule: Rule) => this.shouldRemoveSimpleAddressRule(rule),
    entity_zip: (rule: Rule) => this.isEmptyRule(rule),
    entity_city_state: (rule: Rule) => this.isEmptyRule(rule),
    tag_category_responses: (rule: Rule) => this.isEmptyRule(rule) || isNilOrEmpty(rule.value.tag_category_ids),
    entity_state: (rule: Rule) => this.isEmptyRule(rule),
    entity_electoral_districts: (rule: Rule) => this.isEmptyRule(rule),
    tags_advanced: (rule: Rule) => this.shouldRemoveTagsAdvancedRule(rule),
    saved_query: (rule: Rule) => this.isEmptyRule(rule),
    contact_attempt: (rule: Rule) => this.shouldRemoveContactAttemptRule(rule),
    noop: (rule: Rule) => this.shouldRemoveNoopRool(rule),
    name: (rule: Rule) => this.shouldRemoveNameRule(rule),
    entity_connection_subquery: (rule: Rule) => this.shouldRemoveEntityConnectionRule(rule),
    calendar_event_response_subquery: (rule: Rule) => this.shouldRemoveCalendarEventResponseSubqueryRule(rule),
    // Deprecated connections_subquery
    connections_subquery: (rule: Rule) => this.shouldRemoveConnectionsSubqueryRule(rule),
  };

  constructor(private ruleset: RuleSet) {
    // Create a copy
    this.ruleset = _cloneDeep(ruleset);
  }

  /**
   * Elminates redundant or empty parts of a query object.
   */
  reduce(): RuleSet {
    if (this.ruleset == null) {
      return null;
    }
    const result = this.reduceRules(this.ruleset.rules || []);
    this.ruleset.rules = result || [];
    return this.ruleset;
  }


  /**
   * Recursively reduce rules
   */
  reduceRules(rules: any): any {
    if (isNilOrEmpty(rules)) {
      return null;
    }

    return rules
      .map((rule: Rule | RuleSet) => this.reduceSingleRule(rule))
      .filter(r => !isNilOrEmpty(r));
  }


  private reduceSingleRule(rule: Rule | RuleSet): Rule | RuleSet | null {
    const ruleSet = (rule as RuleSet);
    const ruleSimple = (rule as Rule);

    if (ruleSet.condition) {
      ruleSet.rules = this.reduceRules(ruleSet.rules);
      return isNilOrEmpty(ruleSet.rules) ? null : ruleSet;
    }

    if (ruleSimple && ruleSimple.field && this.field_reducers[ruleSimple.field]) {
      const shouldRemoveRule = this.field_reducers[ruleSimple.field](ruleSimple);
      return shouldRemoveRule ? null : rule;
    }

    return rule;
  }

  fieldsWithRules() {
    return this.reduce().rules.map((r: any) => r.field || r['ui_condition_field']);
  }

  private shouldRemoveNoopRool(_rule: Rule) {
    return true;
  }

  private shouldRemoveTagsAdvancedRule(rule: Rule) {
    return isNilOrEmpty(rule?.value?.tags);
  }

  private shouldRemoveContactAttemptRule(rule: Rule) {
    return isNilOrEmpty(rule?.operator) || isNilOrEmpty(rule?.value?.disposition);
  }

  private shouldRemoveEntityConnectionRule(rule: Rule) {
    if (isNilOrEmpty(rule?.value)) {
      return true;
    }

    if (rule.value.has_connections === true) {
      return false;
    }

    const value: IEntityConnectionSubqueryQueryField['value'] = rule.value;

    const coreValues = values(
      pick(value, [
        'created_by',
        'relative_offset',
        'created_after_date',
        'created_before_date',
      ])
    );

    const hasCoreValues = coreValues.some(v => !isNilOrEmpty(v));

    if (hasCoreValues) {
      return false;
    }

    const subqueryRules = this.reduceRules(rule.value.subquery?.rules) || [];

    return subqueryRules.length === 0;
  }

  /** Depcrecated, always remove */
  private shouldRemoveConnectionsSubqueryRule(rule: Rule) {
    return true;
  }

  private shouldRemoveCalendarEventResponseSubqueryRule(rule: Rule) {
    if (isNilOrEmpty(rule?.value)) {
      return true;
    }

    const value: ICalendarEventResponseSubqueryQueryField['value'] = rule.value;

    const coreValues = values(
      pick(value, [
        'created_by',
        'rsvp_after_date',
        'rsvp_before_date',
        'rsvp_relative_offset',
        'calendar_event_ids',
        'rsvp_statuses',
        'min_calendar_event_responses',
        'max_calendar_event_responses'])
    );

    const hasCoreValues = coreValues.some(v => !isNilOrEmpty(v));

    if (hasCoreValues) {
      return false;
    }

    const subqueryRules = this.reduceRules(rule.value.subquery?.rules) || [];

    return subqueryRules.length === 0;
  }

  // == Field Reducers == //

  /**
   * Shared by simple_email, simple_phone
   *
   * @param rule Rule to check
   */
  private shouldRemoveSimpleContactInfoRule(rule: Rule) {
    if (isNilOrEmpty(rule?.value) || isNilOrEmpty(rule?.operator)) {
      return true;
    }

    if (isNilOrEmpty(rule.value.status)
      && isNilOrEmpty(rule.value.type)
      && isNilOrEmpty(rule.value.subscribed)) {
      return true;
    }
  }

  private shouldRemoveSimpleSocialRule(rule: Rule) {
    if (isNilOrEmpty(rule?.value) || isNilOrEmpty(rule?.operator)) {
      return true;
    }

    if (isNilOrEmpty(rule.value.status)
      && isNilOrEmpty(rule.value.type)) {
      return true;
    }
  }

  private shouldRemoveSimpleAddressRule(rule: Rule) {
    if (isNilOrEmpty(rule?.value) || isNilOrEmpty(rule?.operator)) {
      return true;
    }

    if (
      isNilOrEmpty(rule.value.status) &&
      isNilOrEmpty(rule.value.geocode_bad) &&
      isNilOrEmpty(rule.value.geocodeBad) &&
      isNilOrEmpty(rule.value.mappable)) {
      return true;
    }

    return false;
  }

  private shouldRemoveLastUpdatedOffset(rule: Rule): boolean {
    if (isNilOrEmpty(rule?.value) || isNilOrEmpty(rule.operator)) {
      return true;
    }

    return false;
  }

  private shouldRemoveAssessmentAdvancedRule(rule: Rule): boolean {
    return isNilOrEmpty(rule?.value?.levels);
  }

  private shouldRemoveFollowUpRule(rule: Rule): boolean {
    return this.isEmptyRule(rule) || rule?.value === false;
  }

  private shouldRemoveNameRule(rule: Rule): boolean {
    if (this.isEmptyRule(rule)) {
      return true;
    }

    const value: NameQueryFieldValue = rule.value;

    // Check for any populated name fields
    const hasNameValues = Boolean(
      value.first_name ||
      value.middle_name ||
      value.last_name ||
      value.nickname ||
      value.suffix
    );

    // Check for any "blank" constraint fields
    const hasBlankConstraints = Boolean(
      value.middle_name_blank ||
      value.last_name_blank ||
      value.nickname_blank ||
      value.suffix_blank
    );

    // Keep the rule if it has any meaningful constraints
    return !(hasNameValues || hasBlankConstraints);
  }

  private isEmptyRule(rule: Rule) {
    return (isNilOrEmpty(rule?.value));
  }

}
